import { useState } from "react";

/*
 * Inline SVG charts. No chart library: these two forms are all the panel needs,
 * and hand-rolling them keeps the mark specs (thin marks, 4px rounded data-ends
 * anchored to the baseline, 2px lines, recessive grid, one axis) exact.
 *
 * Both ship a hover layer by default -- a per-bar tooltip and a line crosshair.
 * Series colours are the validated categorical slots 1-3.
 */

const SERIES = ["var(--s1)", "var(--s2)", "var(--s3)"];

const fmt = new Intl.NumberFormat("en-US");

/** Rounded "nice" axis maximum, so ticks land on readable numbers. */
function niceMax(v: number): number {
  if (v <= 0) return 1;
  const mag = 10 ** Math.floor(Math.log10(v));
  const n = v / mag;
  const step = n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10;
  return step * mag;
}

const shortNum = (n: number): string =>
  n >= 1_000_000 ? `${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}M`
  : n >= 1_000 ? `${(n / 1_000).toFixed(n % 1_000 === 0 ? 0 : 1)}k`
  : String(n);

interface Tip {
  x: number;
  y: number;
  rows: { label: string; value: string; color?: string }[];
  title: string;
}

function Tooltip({ tip }: { tip: Tip | null }) {
  if (!tip) return null;
  return (
    <div className="tip" style={{ left: tip.x, top: tip.y }} role="tooltip">
      <div style={{ fontWeight: 600, marginBottom: tip.rows.length ? 4 : 0 }}>{tip.title}</div>
      {tip.rows.map((r) => (
        <div className="tip-row" key={r.label}>
          {r.color ? (
            <span className="legend-swatch" style={{ background: r.color }} aria-hidden="true" />
          ) : null}
          {r.label}: <strong>{r.value}</strong>
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ bars --- */

export interface BarDatum {
  label: string;
  value: number;
  sub?: string;
}

export function BarChart({
  data,
  height = 220,
  valueLabel = "Value",
  viewWidth = 720,
}: {
  data: BarDatum[];
  height?: number;
  valueLabel?: string;
  /**
   * viewBox width. The SVG scales to its container preserving aspect ratio, so
   * a fixed frame in a full-width card scaled the HEIGHT up with it -- the
   * same chart was twice as tall in a full-width card as in a half-width one.
   * Pass a wider frame for wide containers to keep the proportions sane.
   */
  viewWidth?: number;
}) {
  const [tip, setTip] = useState<Tip | null>(null);

  if (!data.length) {
    return <div className="state">No data in this period</div>;
  }

  const W = viewWidth;
  const padL = 46;
  const padR = 8;
  const padT = 10;
  const padB = 26;
  const innerW = W - padL - padR;
  const innerH = height - padT - padB;

  const max = niceMax(Math.max(...data.map((d) => d.value)));
  const slot = innerW / data.length;
  // A 2px surface gap between adjacent bars, so fills never touch. The cap
  // scales with how much room each bar has: at 28px a two-category chart drew
  // two hairlines lost in a wide empty frame, which read as broken rather than
  // sparse. Bars stay thin where there are many of them.
  const barW = Math.max(2, Math.min(56, slot - 8));

  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(max * f));

  return (
    <div className="chart-wrap">
      <svg
        className="chart"
        viewBox={`0 0 ${W} ${height}`}
        role="img"
        aria-label={`${valueLabel} by period`}
        onMouseLeave={() => setTip(null)}
      >
        {ticks.map((t) => {
          const y = padT + innerH - (t / max) * innerH;
          return (
            <g key={t}>
              <line x1={padL} x2={W - padR} y1={y} y2={y} stroke="var(--grid)" strokeWidth="1" />
              <text x={padL - 8} y={y + 4} textAnchor="end" fontSize="10" fill="var(--ink-muted)">
                {shortNum(t)}
              </text>
            </g>
          );
        })}

        {data.map((d, i) => {
          const h = max > 0 ? (d.value / max) * innerH : 0;
          const x = padL + i * slot + (slot - barW) / 2;
          const y = padT + innerH - h;
          return (
            <g key={`${d.label}-${i}`}>
              {/* Full-height hit target: bigger than the mark itself. */}
              <rect
                x={padL + i * slot}
                y={padT}
                width={slot}
                height={innerH}
                fill="transparent"
                onMouseEnter={() =>
                  setTip({
                    // Percentages, so the tooltip tracks the bar as the SVG
                    // scales with its container.
                    x: ((x + barW / 2) / W) * 100,
                    y: y - 8,
                    title: d.sub ?? d.label,
                    rows: [{ label: valueLabel, value: fmt.format(d.value) }],
                  })
                }
              />
              <rect
                x={x}
                y={y}
                width={barW}
                height={Math.max(h, d.value > 0 ? 2 : 0)}
                rx="4"
                ry="4"
                fill="var(--s1)"
              />
            </g>
          );
        })}

        <line
          x1={padL} x2={W - padR}
          y1={padT + innerH} y2={padT + innerH}
          stroke="var(--border-strong)" strokeWidth="1"
        />

        {data.map((d, i) =>
          // Thin out tick labels so they never collide.
          i % Math.ceil(data.length / 15) === 0 ? (
            <text
              key={`t-${i}`}
              x={padL + i * slot + slot / 2}
              y={height - 8}
              textAnchor="middle"
              fontSize="10"
              fill="var(--ink-muted)"
            >
              {d.label}
            </text>
          ) : null
        )}
      </svg>
      {tip ? (
        <div className="tip" style={{ left: `${tip.x}%`, top: (tip.y / height) * 100 + "%" }} role="tooltip">
          <div style={{ fontWeight: 600, marginBottom: 4 }}>{tip.title}</div>
          {tip.rows.map((r) => (
            <div key={r.label}>{r.label}: <strong>{r.value}</strong></div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/* ----------------------------------------------------------------- lines --- */

export interface LineSeries {
  name: string;
  values: number[];
}

export function LineChart({
  labels,
  series,
  height = 240,
  viewWidth = 760,
}: {
  labels: string[];
  series: LineSeries[];
  height?: number;
  /** See BarChart's viewWidth: keeps a wide card from producing a tall chart. */
  viewWidth?: number;
}) {
  const [hover, setHover] = useState<number | null>(null);

  if (!labels.length || !series.length) {
    return <div className="state">No data in this period</div>;
  }

  const W = viewWidth;
  const padL = 48;
  const padR = 12;
  const padT = 12;
  const padB = 28;
  const innerW = W - padL - padR;
  const innerH = height - padT - padB;

  const max = niceMax(Math.max(1, ...series.flatMap((s) => s.values)));
  // A single point would divide by zero; place it mid-axis instead.
  const stepX = labels.length > 1 ? innerW / (labels.length - 1) : 0;
  const xAt = (i: number) => padL + (labels.length > 1 ? i * stepX : innerW / 2);
  const yAt = (v: number) => padT + innerH - (v / max) * innerH;

  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(max * f));

  return (
    <div className="chart-wrap">
      <svg
        className="chart"
        viewBox={`0 0 ${W} ${height}`}
        role="img"
        aria-label="Token usage by day"
        onMouseLeave={() => setHover(null)}
      >
        {ticks.map((t) => {
          const y = yAt(t);
          return (
            <g key={t}>
              <line x1={padL} x2={W - padR} y1={y} y2={y} stroke="var(--grid)" strokeWidth="1" strokeDasharray="2 3" />
              <text x={padL - 8} y={y + 4} textAnchor="end" fontSize="10" fill="var(--ink-muted)">
                {shortNum(t)}
              </text>
            </g>
          );
        })}

        {hover !== null ? (
          <line
            x1={xAt(hover)} x2={xAt(hover)}
            y1={padT} y2={padT + innerH}
            stroke="var(--border-strong)" strokeWidth="1"
          />
        ) : null}

        {series.map((s, si) => (
          <polyline
            key={s.name}
            points={s.values.map((v, i) => `${xAt(i)},${yAt(v)}`).join(" ")}
            fill="none"
            stroke={SERIES[si % SERIES.length]}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ))}

        {/* Point markers on a short series. A two-day trend drawn as a bare
            polyline is a single diagonal stroke with nothing to show where the
            readings actually are. Suppressed on long series, where a marker per
            day becomes noise. */}
        {labels.length <= 12
          ? series.map((s, si) =>
              s.values.map((v, i) => (
                <circle
                  key={`m-${s.name}-${i}`}
                  cx={xAt(i)}
                  cy={yAt(v)}
                  r="4"
                  fill={SERIES[si % SERIES.length]}
                  stroke="var(--surface)"
                  strokeWidth="2"
                />
              ))
            )
          : null}

        {hover !== null
          ? series.map((s, si) => (
              <circle
                key={`p-${s.name}`}
                cx={xAt(hover)}
                cy={yAt(s.values[hover] ?? 0)}
                r="4.5"
                fill={SERIES[si % SERIES.length]}
                /* 2px surface ring keeps overlapping markers readable. */
                stroke="var(--surface)"
                strokeWidth="2"
              />
            ))
          : null}

        <line
          x1={padL} x2={W - padR}
          y1={padT + innerH} y2={padT + innerH}
          stroke="var(--border-strong)" strokeWidth="1"
        />

        {labels.map((l, i) =>
          i % Math.ceil(labels.length / 10) === 0 ? (
            <text key={`l-${i}`} x={xAt(i)} y={height - 8} textAnchor="middle" fontSize="10" fill="var(--ink-muted)">
              {l}
            </text>
          ) : null
        )}

        {/* Column-wide hit targets, laid last so they sit above the marks. */}
        {labels.map((_, i) => (
          <rect
            key={`h-${i}`}
            x={xAt(i) - (stepX || innerW) / 2}
            y={padT}
            width={stepX || innerW}
            height={innerH}
            fill="transparent"
            onMouseEnter={() => setHover(i)}
          />
        ))}
      </svg>

      {hover !== null ? (
        <div
          className="tip"
          style={{
            left: `${(xAt(hover) / W) * 100}%`,
            top: `${(padT / height) * 100}%`,
          }}
          role="tooltip"
        >
          <div style={{ fontWeight: 600, marginBottom: 4 }}>{labels[hover]}</div>
          {series.map((s, si) => (
            <div className="tip-row" key={s.name}>
              <span className="legend-swatch" style={{ background: SERIES[si % SERIES.length] }} aria-hidden="true" />
              {s.name}: <strong>{fmt.format(s.values[hover] ?? 0)}</strong>
            </div>
          ))}
        </div>
      ) : null}

      {/* A legend is always present for two or more series. */}
      {series.length > 1 ? (
        <div className="chart-legend">
          {series.map((s, si) => (
            <span key={s.name}>
              <span className="legend-swatch" style={{ background: SERIES[si % SERIES.length] }} aria-hidden="true" />
              {s.name}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** Labelled horizontal meter: quality metrics and cost breakdown. */
export function Meter({
  label,
  value,
  max = 100,
  display,
  color = "var(--seq-fill)",
}: {
  label: string;
  value: number;
  max?: number;
  display?: string;
  color?: string;
}) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div className="meter-row">
      <span className="meter-label">{label}</span>
      <span className="meter-value">{display ?? `${value.toFixed(0)}%`}</span>
      <div className="meter-track">
        <div className="meter-fill" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}

export const seriesColor = (i: number) => SERIES[i % SERIES.length];
export { Tooltip };
